import type { ReactNode } from 'react';
import { CheckIcon, LockSimpleIcon, XIcon } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ResponsiveDrawer } from '@/components/ui/responsive-drawer';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { CategoryTile } from '@/components/CategoryTile';
import { getCategoryMeta } from '@/components/transactions/screen/categoryIndex';
import { useCategoryIndex } from '@/hooks/useCategoryIndex';
import { useSpaceExpenseRemoval } from '@/hooks/useSpaceExpenseRemoval';
import { PREVIEW_ACTIONS_NOTICE, useSpaceActionsHidden, useSpacePerspective } from '@/hooks/useSpacePartnerPreview';
import { expenseAdjustHref, expenseDate, expenseMemberName, expensePayerName, expensePermissions, memberColor, type SpaceExpenseRow } from '@/lib/spaces/expenses';
import { memberPhoto } from '@/lib/spaces/settlement';
import type { SpaceDetail } from '@/types/spaces';
import { ExpenseEntry, ExpensePills, ExpenseSplitBar } from './SpaceExpenseParts';
import { SettlementAvatar, SettlementMoney } from './SettlementParts';
import { SpacePrivate } from './SpacePartnerPreview';

type Remove = (expense: SpaceExpenseRow, exception: boolean) => Promise<void>;
type Split = SpaceExpenseRow['splits'][number];
const EYEBROW = 'font-mono text-2xs font-medium uppercase tracking-wider text-text-secondary';

export function SpaceExpenseDrawer({ space, userId, expense, model, close }: Readonly<{
  space: SpaceDetail; userId: string; expense: SpaceExpenseRow; model: SpaceDetail['model']; close: () => void;
}>) {
  const removal = useSpaceExpenseRemoval(space.id, close);
  const viewer = useSpacePerspective(userId);
  const category = getCategoryMeta(useCategoryIndex(), expense.categoryId);
  const payer = expensePayerName(space, expense);
  const entry = <ExpenseEntry space={space} expense={expense} userId={viewer} />;
  const handleClose = () => { if (!removal.busy) close(); };
  const handleOpenChange = (open: boolean) => { if (!open) handleClose(); };
  const subtitle = [expenseDate(expense.occurredAt, 'long'), category.pathLabel ?? category.label].join(' · ');
  return <ResponsiveDrawer open onOpenChange={handleOpenChange} dismissible={!removal.busy}>
    <DrawerContent className="overflow-clip bg-surface p-0 data-[vaul-drawer-direction=right]:w-full data-[vaul-drawer-direction=right]:sm:max-w-lg">
      <DrawerHeader className="shrink-0 border-b border-border-subtle px-5 py-4 text-left group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left">
        <div className="flex items-center justify-between gap-3"><DrawerTitle>Despesa do espaço</DrawerTitle><Button variant="ghost" size="icon" aria-label="Fechar despesa" disabled={removal.busy} onClick={handleClose}><XIcon aria-hidden className="size-4" /></Button></div>
        <DrawerDescription className="text-text-secondary">{space.name}</DrawerDescription>
      </DrawerHeader>
      <div role="region" aria-label="Detalhes da despesa" tabIndex={0} className="@container min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring focus-visible:outline-none" data-vaul-no-drag aria-busy={removal.busy}>
        <div className="flex items-center gap-3"><CategoryTile meta={category} size="lg" />
          <div className="min-w-0 flex-1"><h3 className="break-words text-lg font-bold text-text-primary">{expense.name}</h3><p className="text-xs text-text-secondary">{subtitle}</p></div>
        </div>
        <div className="flex flex-wrap gap-2 empty:hidden"><ExpensePills expense={expense} /></div>
        <ExpenseNote expense={expense} payer={payer} />
        <dl className="grid gap-2 text-center @xs:grid-cols-3">
          <Fact label="Quem pagou"><span className="break-words text-sm font-semibold">{payer}</span></Fact>
          <Fact label="No espaço"><SettlementMoney cents={expense.sharedAmountCents} className="text-sm font-semibold text-text-primary" /></Fact>
          <Fact label="Como entrou"><span className="break-words text-xs font-semibold">{entry}</span></Fact>
        </dl>
        <Division space={space} expense={expense} model={model} />
        <Visibility space={space} expense={expense} model={model} payer={payer} mine={expense.payerUserId === viewer} />
        <section aria-label="Histórico"><h4 className={EYEBROW}>Histórico</h4>
          <ul className="mt-2 space-y-1 text-2xs text-text-secondary">
            <li>{expenseDate(expense.createdAt, 'long')} · {entry}</li>
            <li>id no espaço: <span className="break-all font-mono">{expense.id}</span>. Continua o mesmo se o banco sincronizar de novo.</li>
          </ul>
        </section>
      </div>
      <Footer space={space} userId={userId} expense={expense} busy={removal.busy} remove={removal.remove} />
    </DrawerContent>
  </ResponsiveDrawer>;
}

function Fact({ label, children }: Readonly<{ label: string; children: ReactNode }>) {
  return <div className="rounded-xl bg-surface-sunken p-3"><dt className={EYEBROW}>{label}</dt><dd className="mt-2">{children}</dd></div>;
}

function ExpenseNote({ expense, payer }: Readonly<{ expense: SpaceExpenseRow; payer: string }>) {
  if (!expense.note) return null;
  return <p className="break-words rounded-xl bg-surface-sunken px-3 py-2 text-xs text-text-secondary">“{expense.note}”, {payer}</p>;
}

function Division({ space, expense, model }: Readonly<{ space: SpaceDetail; expense: SpaceExpenseRow; model: SpaceDetail['model'] }>) {
  const renderShare = (split: Split) => <li key={split.userId} className="flex items-center gap-2 py-2 text-xs">
    <SettlementAvatar name={expenseMemberName(space, expense, split.userId)} photoUrl={memberPhoto(space, split.userId)} className="size-6 text-2xs" badgeClassName={memberColor(space, split.userId)} />
    <span className="min-w-0 flex-1 break-words font-medium text-text-primary">{expenseMemberName(space, expense, split.userId)}</span><SettlementMoney cents={split.amountCents} />
  </li>;
  if (model === 'assigned') return <p className="rounded-xl border border-border-subtle p-3 text-xs text-text-secondary">Despesa atribuída a {expensePayerName(space, expense)}.</p>;
  return <section aria-label="Parte de cada pessoa" className="rounded-xl border border-border-subtle p-3">
    <h4 className={EYEBROW}>Parte de cada pessoa</h4>
    <div className="mt-2"><ExpenseSplitBar space={space} splits={expense.splits} /></div>
    <ul className="mt-1 divide-y divide-border-subtle">{expense.splits.map(renderShare)}</ul>
    <p className="mt-2 text-2xs leading-relaxed text-text-secondary">Divisão pelos percentuais do espaço. Depois de um acerto registrado, a despesa mantém os percentuais daquele momento.</p>
  </section>;
}

function Visibility({ space, expense, model, payer, mine }: Readonly<{ space: SpaceDetail; expense: SpaceExpenseRow; model: SpaceDetail['model']; payer: string; mine: boolean }>) {
  return <section aria-label="Quem vê o quê"><h4 className={EYEBROW}>Quem vê o quê</h4>
    <ul className="mt-2 space-y-2 text-xs text-text-secondary">
      <li className="flex gap-2"><CheckIcon aria-hidden weight="bold" className="mt-0.5 size-3 shrink-0 text-success-text" /><span>Todas as {space.members.length} pessoas do espaço: nome, categoria, valor no espaço, como entrou{model === 'split' ? ', quem pagou e a parte de cada pessoa' : ' e quem pagou'}.</span></li>
      <li className="flex gap-2"><LockSimpleIcon aria-hidden weight="bold" className="mt-0.5 size-3 shrink-0" /><PrivateDetails expense={expense} payer={payer} mine={mine} /></li>
    </ul>
  </section>;
}

// `source` only reaches the payer's own client. Previewing as another member, it is still shown, blurred, under
// the payer's name: the facts are the viewer's, the words are the previewed member's.
function PrivateDetails({ expense, payer, mine }: Readonly<{ expense: SpaceExpenseRow; payer: string; mine: boolean }>) {
  const owner = mine ? 'você' : payer;
  if (!expense.source) return <span className="min-w-0 break-words">Só {owner}: a conta usada{expense.partial ? ' e o valor total da compra' : ''}.</span>;
  return <span className="min-w-0 break-words">Só {owner}: <SpacePrivate>{expense.source.account?.name ?? 'conta não disponível'}</SpacePrivate><PrivateTotal expense={expense} /></span>;
}

function PrivateTotal({ expense }: Readonly<{ expense: SpaceExpenseRow }>) {
  if (!expense.partial || !expense.source) return null;
  return <> · total <SpacePrivate><SettlementMoney cents={expense.source.totalAmountCents} /></SpacePrivate></>;
}

function Footer({ space, userId, expense, busy, remove }: Readonly<{
  space: SpaceDetail; userId: string; expense: SpaceExpenseRow; busy: boolean; remove: Remove;
}>) {
  const allowed = expensePermissions(space, expense, userId);
  const previewing = useSpaceActionsHidden();
  const handleRemove = () => { void remove(expense, false); };
  if (!allowed.remove) return <p className="shrink-0 border-t border-border-subtle bg-surface px-5 py-4 text-xs text-text-secondary">{previewing ? PREVIEW_ACTIONS_NOTICE : readOnlyReason(space)}</p>;
  return <div className="flex shrink-0 flex-wrap gap-2 border-t border-border-subtle bg-surface px-5 py-4">
    <Button variant="destructive" size="sm" disabled={busy} onClick={handleRemove}>{busy ? 'Tirando…' : 'Tirar do espaço'}</Button>
    <ExceptButton visible={allowed.except} busy={busy} expense={expense} remove={remove} />
    <AdjustLink href={expenseAdjustHref(expense, userId)} />
  </div>;
}

// Changing what was shared happens where it was sent from: the payer's own transaction, in the personal view.
function AdjustLink({ href }: Readonly<{ href: string | null }>) {
  if (!href) return null;
  return <Button asChild variant="outline" size="sm"><Link to={href}>Ajustar nas transações</Link></Button>;
}

function ExceptButton({ visible, busy, expense, remove }: Readonly<{ visible: boolean; busy: boolean; expense: SpaceExpenseRow; remove: Remove }>) {
  const handleExcept = () => { void remove(expense, true); };
  if (!visible) return null;
  return <Button variant="ghost" size="sm" disabled={busy} onClick={handleExcept} title="Cria uma exceção para este estabelecimento neste espaço">Tirar e não repetir</Button>;
}

function readOnlyReason(space: SpaceDetail): string {
  if (space.archivedAt) return 'Espaço encerrado. As despesas continuam disponíveis para consulta.';
  return 'Você tem acesso somente de leitura. Um editor pode tirar esta despesa do espaço.';
}
